import { Component, input, OnInit, output, signal } from '@angular/core';
import { ServiceCatalogRedirection, ServiceProviderInfo, VendorDetailsResponse, VendorListPayload } from 'src/app/models/request-service.model';
import { GoogleMapsService } from 'src/app/services/google-maps/google-maps.service';
import { RequestBookingService } from 'src/app/services/request-booking/request-booking.service';
import { Geolocation } from '@capacitor/geolocation';
import { RequestServiceProvider } from 'src/app/services/providers/request-service-provider/request-service-provider';
import { CoreService } from 'src/app/services/core/core.service';
import { TranslateService } from '@ngx-translate/core';
import { translateValue } from 'src/app/helpers/translate/translateHelper';
import { LaunchNavigator } from '@capgo/capacitor-launch-navigator';
import { RequestBookingHelperService } from 'src/app/services/request-booking-helper/request-booking-helper.service';
import { Browser } from '@capacitor/browser';

@Component({
  selector: 'app-vendor-service-list',
  templateUrl: './vendor-service-list.component.html',
  styleUrls: ['./vendor-service-list.component.scss'],
  standalone: false
})
export class VendorServiceListComponent implements OnInit {

  vendoreService = input.required<ServiceCatalogRedirection | null>();
  isVendorInfo = input.required<boolean>();
  serviceProviderInfo = input.required<ServiceProviderInfo | null>();
  icnNo = input.required<number>();

  query = signal<string>('');
  places = signal<google.maps.places.AutocompletePrediction[]>([]);
  autocompleteService!: google.maps.places.AutocompleteService;
  latitude = signal<number>(0);
  longitude = signal<number>(0);
  recordPerPageLimit = signal<number>(10);
  pageOffset = signal<number>(0);
  vendorList = signal<VendorDetailsResponse[]>([]);
  validationMsg = signal<string>('');

  valueChange = output<void>();
  selectSearch: boolean = false;

  constructor(
    private readonly mapsLoader: GoogleMapsService,
    private readonly translateService: TranslateService,
    private readonly coreService: CoreService,
    private readonly requestBookingService: RequestBookingService,
    private readonly requestServiceProvider: RequestServiceProvider,
    private readonly requestBookingHelper: RequestBookingHelperService
  ) { }

  /**
   * Initializes location, loads Google Maps SDK and sets up autocomplete service.
   */
  async ngOnInit() {
    this.initPage();
  }

  async initPage() {
    this.getCurrentLoaction();
    await this.mapsLoader.loadGoogleMaps();
    this.autocompleteService = new google.maps.places.AutocompleteService();
  }

  /**
   * Fetches current location from cache or device geolocation and triggers vendor list loading.
   */
  async getCurrentLoaction() {
    try {
      const {
        latitude,
        longitude
      } = await this.requestBookingHelper.getCurrentLocation();
      this.latitude.set(latitude);
      this.longitude.set(longitude);
      this.getVendorList();
    }
    catch (error) {
      this.latitude.set(0);
      this.longitude.set(0);
      this.requestServiceProvider.setCurrentLatitude(null);
      this.requestServiceProvider.setCurrentLatitude(null);
      this.validationMsg.set('Please enter a location');
      throw error;
    }
  }

  /**
   * Handles search input and retrieves Google Places autocomplete predictions.
   */
  async searchPlace(event: any) {
    // Sync query signal with ion-searchbar input event
    if (event?.detail?.data && !this.query()) {
      this.query.set(event.detail.data);
    }

    const queryValue = this.query();

    // Guard against short queries or uninitialized autocomplete service
    if (!queryValue || queryValue.length <= 1 || !this.autocompleteService) {
      this.places.set([]);
      return;
    }

    const places = await this.requestBookingHelper.getPlacesPrediction(queryValue, this.autocompleteService);
    this.places.set(places);

  }

  /**
   * Handles place selection from autocomplete suggestions and triggers geocoding.
   */
  async selectPlace(place: google.maps.places.AutocompletePrediction) {
    this.places.set([]);
    this.query.set(place.description);

    const {
      latitude,
      longitude
    } = await this.requestBookingHelper.geocodeAddress(place.description);
    this.latitude.set(latitude);
    this.longitude.set(longitude);
    this.selectSearch = false;
    this.getVendorList();
  }



  /**
   * Loads vendor list based on location, pagination and selected vendor service context.
   */
  getVendorList(pageOffset?: number, event?: any) {

    this.pageOffset.update(x => pageOffset ? pageOffset : 0);

    let postObj: VendorListPayload = {
      icnNo: this.icnNo(),
      vendor_service_id: this.isVendorInfo()
        ? (this.serviceProviderInfo()?.vendoR_SERVICE_ID || 0)
        : (this.vendoreService()?.vendoR_SERVICE_ID || 0),
      latitude: this.latitude(),
      longitude: this.longitude(),
      page_Limit: this.recordPerPageLimit(),
      page_Offset: pageOffset ? pageOffset : this.pageOffset(),
      radius_Miles: 50
    };

    this.requestBookingService.getVendoreList(postObj).subscribe({
      next: (response) => {
        if (!response) {
          return;
        }

        // Handle infinite scroll end when no more records are available
        if (pageOffset != undefined && response.length == 0) {
          event?.target.complete();
          event.target.disabled = true;
          return;
        }

        // Append or replace vendor list based on pagination state
        if (pageOffset != undefined) {
          this.vendorList.update(list => [...list, ...response]);
        }
        else {
          this.vendorList.set(response);
        }

        // Set validation message when no vendors are found
        if (!this.vendorList().length) {
          this.validationMsg.set(this.translateStr('NO_SERVICE_VALIDATION'));
        } else {
          this.validationMsg.set('');
        }
      },
      complete: () => {
        event?.target.complete();
      }
    });
  }

  /**
   * Triggers pagination to load additional vendors on infinite scroll.
   */
  loadMoreVendor(event: any) {
    if (!this.vendorList() || this.vendorList().length == 0) {
      return;
    }

    this.getVendorList(this.pageOffset() + 1, event);
  }

  /**
   * Emits back navigation event to parent component.
   */
  backBtnClicked() {
    this.valueChange.emit();
  }

  /**
   * Opens external maps or native navigation based on platform environment.
   */
  async goToDirectionPage(vendor: any) {
    try {
      if (this.coreService.isIos) {
        await Browser.open({
          url: `https://maps.apple.com/?daddr=${vendor.latitude},${vendor.longitude}`
        });
      } else {
        await LaunchNavigator.navigate({
          destination: [vendor.latitude, vendor.longitude]
        });
      }
    } catch (error) {
      console.error('Navigation error', error);
    }
  }

  /**
   * Translates validation and UI strings using translation helper.
   */
  translateStr(val: string) {
    return translateValue(val, this.translateService);
  }

  startSelection() {
    this.selectSearch = true;
  }

  onBlur() {
    if (!this.selectSearch) {
      this.places.set([]);
      this.query.set('');
    }
  }
}